/**
 * Galeria de tableros: cards de los tableros estaticos publicados.
 *
 * Solo tableros estaticos (CIMS0112/CIMS0114). El tablero del Comite de Gerencia salio de aqui:
 * es un componente React, no un HTML subido, asi que vive en la parametrica de graficas
 * (CIMS0036/CIMS0037) y lo pinta Bugraphs en el Home. root/gestionsiaco#13
 */
import { Body, Box, Button, Flexbox, Heading, TableLoader } from "codekit";
import { useState } from "react";

import Visordas from "./Visordas";
import { useGetVisiblesQuery } from "../../libs/redux/slices/Paramgen/Dashestx/dashestxApiSlice";

const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// Estas cards son instantaneas mensuales: lo que el usuario busca de un vistazo es de que
// mes es cada tablero, asi que el periodo va como distintivo y no como texto al pie.
// La sombra en reposo es la que da relieve a la card; el hover solo la profundiza,
// asi que las cards se ven levantadas aunque el cursor este en otro lado.
const cardStyle = {
  minWidth: 280,
  flex: "1 1 300px",
  borderLeft: "4px solid",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)",
};
const cardHoverStyle = { transform: "translateY(-2px)", boxShadow: "0 12px 28px rgba(0, 0, 0, 0.16)" };
const badgeStyle = { display: "inline-block", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em" };

/**
 * Convierte un periodo YYYY-MM en una etiqueta legible tipo "AGO 2026"
 * @param {string} period Periodo en formato YYYY-MM
 * @returns {string} Etiqueta del periodo
 */
const formatPeriod = (period) => {
  const [year, month] = (period || "").split("-");
  const name = months[Number(month) - 1];
  return name ? `${name.toUpperCase()} ${year}` : period;
};

/**
 * Convierte una fecha YYYY-MM-DD en "16 ago 2026". Se parte la cadena en vez de usar
 * Date, que la interpreta como UTC y puede mostrar el dia anterior.
 * @param {string} date Fecha en formato YYYY-MM-DD
 * @returns {string} Fecha legible
 */
const formatDate = (date) => {
  const [year, month, day] = (date || "").split("-");
  const name = months[Number(month) - 1];
  return name ? `${Number(day)} ${name} ${year}` : date;
};

const Galerdas = () => {
  const { data: visibles, isLoading, isError } = useGetVisiblesQuery();
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  //Arma el estilo de la card segun si tiene contenido publicado y si esta bajo el cursor
  const getCardStyle = (key, isPublished) => ({
    ...cardStyle,
    borderLeftColor: isPublished ? "#22629B" : "#A3A3A3",
    transition: prefersReducedMotion ? "none" : "transform 150ms ease, box-shadow 150ms ease",
    ...(hovered === key && !prefersReducedMotion ? cardHoverStyle : {}),
  });

  //Distintivo de la esquina: el periodo vigente o el estado cuando aun no hay version
  const renderBadge = (text, isPublished) => (
    <Box
      bg={isPublished ? "secondary-700" : "neutral-300"}
      color={isPublished ? "white" : "neutral-500"}
      rounded="xl"
      spacingX={3}
      spacingY={1}
      style={badgeStyle}
    >
      {text}
    </Box>
  );

  if (selected) {
    return <Visordas dashboard={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <Box spacing={4}>
      <Heading variant="heading-6">Tableros</Heading>
      {isLoading && <TableLoader />}
      <Flexbox gap={5} wrap>
        {(visibles?.data || []).map((dashboard) => (
          <Box
            key={dashboard.DASHIDXX}
            spacing={5}
            rounded="lg"
            bg="white"
            style={getCardStyle(dashboard.DASHIDXX, Boolean(dashboard.VERPERXX))}
            onMouseEnter={() => setHovered(dashboard.DASHIDXX)}
            onMouseLeave={() => setHovered(null)}
          >
            <Flexbox justifyContent="between" alignItems="start" gap={3}>
              <Heading variant="heading-5">{dashboard.DASHNOMX}</Heading>
              {renderBadge(
                dashboard.VERPERXX ? formatPeriod(dashboard.VERPERXX) : "SIN PUBLICAR",
                Boolean(dashboard.VERPERXX)
              )}
            </Flexbox>
            <Body variant="body-2">{dashboard.DASHDESX}</Body>
            <Body variant="body-3">
              {dashboard.REGFECXX ? `Actualizado ${formatDate(dashboard.REGFECXX)}` : "Aun no tiene contenido"}
            </Body>
            <Box mt={4}>
              <Button
                variant="primary-bold"
                size="s"
                type="button"
                disabled={!dashboard.VERPERXX}
                onClick={() => setSelected(dashboard)}
              >
                Abrir
              </Button>
            </Box>
          </Box>
        ))}
      </Flexbox>
      {isError && <Body variant="body-2">No se pudieron cargar los tableros publicados.</Body>}
    </Box>
  );
};

export default Galerdas;
